import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING,
    LIST, ADD, EDIT,
    DANGER, SUCCESS,
    ASC,
} from "const"
import { projectsRoute } from "client/parts/routes"
import { MtProject } from "server/models"
import { StateType, deepCopy, hasAccess } from "client/utils"
import {
    PRM_PROJECT,
    StoreType,
    ProjectList,
    ProjectFilters,

    LoadAllProjects,
    LoadAllProjectUsers,
    SortProjects,
    DeleteProject,
    GetMaxProjects,

    LoadDriveFolderByProjectId,
    LoadDriveFolders,
    LoadDriveFiles,

    setProjectList,
    setProjectIds,
    setProjectSort,
    setProjectFilters,
    setProjectUserList,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    Loader,
    Button,
    Feedback,
    Table,
    TableSearchWrapper,
    SortRows,
    DeleteRow,
    BalloonMessage,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import ProjectSearch from "client/pages/search/projectSearch"
import Columns from "./columns"

const List = () => {
    const projectList = useSelector((state: StoreType) => state.project.projectList)
    const projectIds = useSelector((state: StoreType) => state.project.projectIds)
    const sortColumn = useSelector((state: StoreType) => state.project.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.project.sortOrder)
    const filters = useSelector((state: StoreType) => state.project.filters)
    const reloadList = useSelector((state: StoreType) => state.project.reloadList)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        Promise.all([
            LoadAllProjects(userData.accountId).then((res) => {
                dispatch(setProjectList(res))
            }),
            LoadAllProjectUsers().then((res) => {
                dispatch(setProjectUserList(res))
            }),
        ]).then(() => {
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sortedIds = SortProjects(Object.values(projectList), sortColumn, sortOrder, filters)
        dispatch(setProjectIds(sortedIds))
    }, [projectList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MtProject, order) => {
        dispatch(setProjectSort(column, order))
    }

    const onFilter = (newFilters: ProjectFilters) => {
        dispatch(setProjectFilters(newFilters))
    }

    const onDelete: DeleteRow = (project: MtProject, callback) => {
        LoadDriveFolderByProjectId(project.id).then((projectFolder) => {
            LoadDriveFolders(project.accountId, projectFolder.id)
            .then((folders) => {
                LoadDriveFiles(project.accountId, projectFolder.id, "id", ASC, 0, 10)
                .then((files) => {
                    // project can not be removed if its folder is not empty
                    if (folders.length || files.length) {
                        BalloonMessage(lang.projectFolderNotEmpty)
                        callback(false, null)
                        return
                    }

                    DeleteProject(project.id, userData.accountId).then((removedRows) => {
                        // project folder will be removed automatically
                        const success = removedRows === 1
                        callback(success, () => {
                            const projectListCopy: ProjectList = projectList ? deepCopy(projectList) : {}
                            delete projectListCopy[project.id]
                            dispatch(setProjectList(projectListCopy))
                            dispatch(setProjectIds(projectIds.filter((id) => id !== project.id)))
                        })
                    })
                })
            })
        })
    }

    // max number of projects
    const maxProjects = GetMaxProjects(packageSettings)
    const projectNumber = Object.keys(projectList).length
    let feedback: string
    if (maxProjects === null) {
        feedback = lang.maxProjectsUnlimited
    } else {
        if (projectNumber < maxProjects) feedback = lang.maxProjects
        if (projectNumber === maxProjects) feedback = lang.maxProjectsReached
        if (projectNumber > maxProjects) feedback = lang.maxProjectsExceeded
        feedback = feedback.replace("{0}", maxProjects.toString())
    }

    const maxProjectsReached = maxProjects !== null && projectNumber >= maxProjects
    const maxProjectsExceeded = maxProjects !== null && projectNumber > maxProjects

    const columns = Columns()

    const listAccess = hasAccess(PRM_PROJECT, LIST)
    const addAccess = hasAccess(PRM_PROJECT, ADD)

    if (!listAccess) return <AccessDenied />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.projects}</PageTitle>
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <TableSearchWrapper>
                    <ProjectSearch
                        id="projectList"
                        rightOffset={addAccess}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        addAccess && (
                            <Button
                                variant="success"
                                disabled={maxProjectsReached}
                                onClick={() => dispatch(addModal({ url: `${projectsRoute}/${EDIT}/0` }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={projectList}
                    dataIds={projectIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="md"
                    showPagination
                    onSort={onSort}
                    onDelete={onDelete}
                />
                <PageForm>
                    <Feedback variant={maxProjectsExceeded ? DANGER : SUCCESS}>
                        {feedback}
                    </Feedback>
                </PageForm>
            </PageContent>
        </Container>
    )
}
export default List
